import { Combobox } from "@headlessui/react";
import { Float } from "@headlessui-float/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import React, { useMemo, useRef, useState } from "react";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationManagedAdNetworkDto } from "../../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { DataRecipientLogo } from "../../components/DataRecipientLogo";
import { alphabetically } from "../../surveys/util";
import { isConnectedSystemVendor } from "../../util/vendorNesting";

type PickerOption = {
  /** Data recipient id the vendor's data is retrieved via; null means the vendor itself. */
  id: string | null;
  name: string;
  recipient: OrganizationDataRecipientDto | null;
  disabledReason?: string;
};

/**
 * Picks the recipient a vendor's Access data is retrieved via (DES-43), e.g. FedEx's shipping data from Shopify. Any
 * recipient on the list can be picked, except the vendor itself and recipients already retrieved via another: links
 * stay one level deep, so the Access step remains a flat parent/child list. The Connected System is left out because
 * its Access row is its automation, which has no room for a nested list. Callers can disable more candidates through
 * `unavailableReasons`.
 *
 * Links are org-wide while `vendors` is one context's list, so a saved link to a vendor outside the list is still shown
 * by name rather than as "itself", and picking something else here replaces it.
 */
export function DataRetrievedViaPicker({
  vendor,
  vendors,
  relations,
  unavailableReasons = {},
  onChange,
  compact = false,
}: {
  vendor: OrganizationDataRecipientDto;
  vendors: OrganizationDataRecipientDto[];
  /** Every "Data retrieved via" link for the organization. */
  relations: OrganizationManagedAdNetworkDto[];
  /** Data recipient id → why it can't be picked, shown under its name. */
  unavailableReasons?: Record<string, string>;
  onChange: (managerId: string | null) => Promise<void>;
  /** Table-cell sizing; the side panel uses the full-width field. */
  compact?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [pendingManagerId, setPendingManagerId] = useState<string | null | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const vendorsById = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);

  const options = useMemo<PickerOption[]>(() => {
    const itself: PickerOption = { id: null, name: `${vendor.name} itself`, recipient: null };
    const others = vendors
      .filter((candidate) => candidate.id !== vendor.id && !isConnectedSystemVendor(candidate))
      .sort(alphabetically)
      .map((candidate): PickerOption => {
        const linkedTo = relations.find((relation) => relation.networkId === candidate.id);
        const linkedToName = linkedTo
          ? vendorsById.get(linkedTo.managerId)?.name ?? linkedTo.managerName
          : undefined;
        return {
          id: candidate.id,
          name: candidate.name,
          recipient: candidate,
          disabledReason: linkedTo
            ? `Already retrieved via ${linkedToName}`
            : unavailableReasons[candidate.id],
        };
      });

    const savedLink = relations.find((relation) => relation.networkId === vendor.id);
    const savedOutsideList =
      savedLink && !vendorsById.has(savedLink.managerId)
        ? [
            {
              id: savedLink.managerId,
              name: savedLink.managerName ?? "Another vendor",
              recipient: null,
            },
          ]
        : [];
    return [itself, ...savedOutsideList, ...others];
  }, [relations, unavailableReasons, vendor, vendors, vendorsById]);

  const savedManagerId =
    relations.find((relation) => relation.networkId === vendor.id)?.managerId ?? null;
  const selectedManagerId = pendingManagerId !== undefined ? pendingManagerId : savedManagerId;
  const selected = options.find((option) => option.id === selectedManagerId) ?? options[0];
  const isLinked = selected.id !== null;

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => option.name.toLowerCase().includes(query.toLowerCase()));

  const handleChange = async (option: PickerOption | null) => {
    setQuery("");
    if (!option || option.disabledReason || option.id === selectedManagerId) {
      return;
    }
    setPendingManagerId(option.id);
    try {
      await onChange(option.id);
    } finally {
      setPendingManagerId(undefined);
    }
  };

  return (
    <Combobox value={selected} onChange={handleChange}>
      {({ open }) => (
        <Float portal zIndex={250} placement="bottom-start" offset={4} flip adaptiveWidth>
          <div
            className="relative flex items-center bg-white rounded-md"
            style={{
              border: `1px solid ${open ? "#6E56CF" : "#D1D5DB"}`,
              height: compact ? 32 : 38,
            }}
          >
            {isLinked && (
              <span className="flex flex-shrink-0" style={{ marginLeft: 8 }}>
                <DataRecipientLogo dataRecipient={selected.recipient} size={compact ? 16 : 20} />
              </span>
            )}
            <Combobox.Input
              aria-label={`Data retrieved via, for ${vendor.name}`}
              className={clsx(
                "w-full min-w-0 bg-transparent text-sm focus:outline-none truncate",
                isLinked ? "text-neutral-900" : "text-neutral-500",
              )}
              // The field's own border shows focus; drop the forms plugin's square focus ring.
              style={{ border: 0, boxShadow: "none", padding: "0 4px 0 8px", height: "100%" }}
              displayValue={(option: PickerOption | null) => (open ? "" : option?.name ?? "")}
              placeholder={open ? "Search vendors" : selected.name}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={() => {
                if (!open) {
                  buttonRef.current?.click();
                }
              }}
            />
            <Combobox.Button
              ref={buttonRef}
              className="flex items-center flex-shrink-0 bg-transparent"
              style={{ border: 0, padding: "0 6px", height: "100%" }}
            >
              <ChevronUpDownIcon className="h-5 w-5 text-neutral-500" aria-hidden="true" />
            </Combobox.Button>
          </div>

          <Combobox.Options
            className="tw-boundary"
            style={{ listStyle: "none", margin: 0, padding: 0 }}
          >
            <div
              className="rounded-md bg-white shadow-lg overflow-y-auto py-1"
              style={{ maxHeight: 280, minWidth: 240, border: "1px solid #E5E7EB" }}
            >
              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-sm text-neutral-500">No matching vendors</div>
              )}
              {filteredOptions.map((option) => (
                <Combobox.Option
                  key={option.id ?? "itself"}
                  value={option}
                  disabled={Boolean(option.disabledReason)}
                  className={({ active, disabled }) =>
                    clsx(
                      "flex items-center gap-2 px-3 py-2 text-sm",
                      disabled ? "cursor-not-allowed" : "cursor-pointer",
                      active && !disabled && "bg-primary-50",
                    )
                  }
                >
                  {({ selected: isSelected, disabled }) => (
                    <>
                      {option.recipient ? (
                        <span
                          className="flex flex-shrink-0"
                          style={disabled ? { opacity: 0.5 } : undefined}
                        >
                          <DataRecipientLogo dataRecipient={option.recipient} size={20} />
                        </span>
                      ) : (
                        <span className="flex-shrink-0" style={{ width: 20 }} />
                      )}
                      <span className="flex flex-col min-w-0 flex-1">
                        <span
                          className={clsx(
                            "truncate",
                            disabled ? "text-neutral-400" : "text-neutral-900",
                            !option.recipient && "text-neutral-600",
                          )}
                        >
                          {option.name}
                        </span>
                        {option.disabledReason && (
                          <span className="text-xs text-neutral-400 truncate">
                            {option.disabledReason}
                          </span>
                        )}
                      </span>
                      {isSelected && (
                        <CheckIcon className="h-4 w-4 flex-shrink-0 text-primary-600" />
                      )}
                    </>
                  )}
                </Combobox.Option>
              ))}
            </div>
          </Combobox.Options>
        </Float>
      )}
    </Combobox>
  );
}
